import { useState, useMemo, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { categories, comics } from '@/mocks/comics';
import Breadcrumb from '@/components/base/Breadcrumb';
import type { Comic } from '@/types';

function getCategoryName(slug: string): string {
  const cat = categories.find(c => c.slug === slug);
  return cat ? cat.name : '全部';
}

function ComicCard({ comic }: { comic: Comic }) {
  const statusLabel = comic.status === 'ongoing' ? '连载中' : '已完结';

  return (
    <Link
      to={`/article/${comic.id}`}
      className="group block bg-background-50 border border-background-200/70 rounded-lg overflow-hidden hover:border-primary-300 transition-all cursor-pointer"
    >
      <div className="relative aspect-[3/4] overflow-hidden">
        <img
          src={comic.cover}
          alt={`${comic.title} - 韩漫在线免费阅读`}
          title={`${comic.title} - 韩漫在线免费阅读`}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
        />
        <div className="absolute top-2 right-2">
          <span className={`px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${
            comic.status === 'ongoing' ? 'bg-primary-500 text-background-50' : 'bg-secondary-500 text-background-50'
          }`}>
            {statusLabel}
          </span>
        </div>
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-3">
          <div className="flex items-center gap-1 text-background-50 text-xs">
            <i className="ri-star-fill text-accent-500"></i>
            <span>{comic.rating}</span>
            <span className="mx-1">·</span>
            <span>{comic.chapters}话</span>
          </div>
        </div>
      </div>
      <div className="p-3 md:p-4">
        <h3 className="text-sm md:text-base font-heading font-semibold text-foreground-950 group-hover:text-primary-500 transition-colors line-clamp-1 mb-1">
          {comic.title}
        </h3>
        <p className="text-xs text-foreground-500 mb-2">{comic.author}</p>
        <div className="flex flex-wrap gap-1">
          {comic.tags.slice(0, 2).map(tag => (
            <span key={tag} className="px-2 py-0.5 bg-background-100 text-foreground-600 rounded-full text-xs whitespace-nowrap">
              {tag}
            </span>
          ))}
        </div>
      </div>
    </Link>
  );
}

export default function CategoryPage() {
  const { slug = 'all' } = useParams<{ slug: string }>();
  const categoryName = getCategoryName(slug);
  const mainCategories = categories.filter(c => c.slug !== 'all');

  const filteredComics = useMemo(() => {
    if (slug === 'all') return comics;
    return comics.filter(c => c.category === slug);
  }, [slug]);

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.hanmanwu.com/' },
              { '@type': 'ListItem', position: 2, name: `${categoryName}韩漫` },
            ],
          }),
        }}
      />
      <main className="min-h-screen">
        <div className="bg-background-100 border-b border-background-200/70">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <Breadcrumb items={[{ label: '首页', href: '/' }, { label: `${categoryName}韩漫` }]} />
            <div className="pb-6">
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-2">
                {slug === 'all' ? '全部韩漫作品' : `${categoryName}韩漫`}
              </h1>
              <p className="text-sm text-foreground-500">
                {slug === 'all'
                  ? '浏览全部免费韩国漫画，无删减下拉式阅读体验'
                  : `精选${categoryName}题材韩国漫画，无删减下拉式免费阅读`}
              </p>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 md:px-6 py-6">
          <div className="flex flex-wrap items-center gap-2 mb-8 pb-6 border-b border-background-200/70">
            {categories.map(cat => (
              <Link
                key={cat.id}
                to={`/category/${cat.slug}`}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors cursor-pointer ${
                  slug === cat.slug
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                {cat.name}
              </Link>
            ))}
          </div>

          {filteredComics.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-5" data-product-shop="">
              {filteredComics.map(comic => (
                <ComicCard key={comic.id} comic={comic} />
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100 text-foreground-300">
                <i className="ri-inbox-line text-2xl"></i>
              </div>
              <p className="text-foreground-500">该分类暂无漫画，请查看其他分类</p>
              <Link to="/category/all" className="mt-4 inline-flex items-center gap-1 text-sm text-primary-500 hover:text-primary-600 transition-colors cursor-pointer">
                <i className="ri-arrow-left-line"></i>
                返回全部漫画
              </Link>
            </div>
          )}
        </div>
      </main>
    </>
  );
}